import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { auth } from 'services/firebase';
import { store } from 'services/store';
import { logoutUser } from 'services/store/reducers/authReducer';

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: `${process.env.REACT_APP_API_URL}/se_api/d/v1`
    });

    this.instance.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (!user) {
        return config;
      }

      const token = await user.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    
      return config;
    });

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
    
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
    
          const user = auth.currentUser;
          if (user) {
            try {
              const newToken = await user.getIdToken(true);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              await this.post('/auth/activity', {
                type: 'ACCESS_UPDATE',
              });
              return this.instance(originalRequest);
            } catch (refreshError) {
              store.dispatch(logoutUser());
              return Promise.reject(refreshError);
            }
          } else {
            store.dispatch(logoutUser());
          }
        }
    
        return Promise.reject(error);
      }
    );    
  }

  public get<T>(path: string, params?: { [key: string]: any }): Promise<AxiosResponse<T>> {
    return this.instance.get(path, { params });
  }

  public post<T, R>(
    path: string,
    data: T,
    config?: InternalAxiosRequestConfig
  ): Promise<AxiosResponse<R>> {
    return this.instance.post(path, data, config);
  }

  public put<T, R>(path: string, data: T): Promise<Promise<AxiosResponse<R>>> {
    return this.instance.put(path, data);
  }

  public patch<T, R>(
    path: string,
    data: T,
    config?: InternalAxiosRequestConfig
  ): Promise<AxiosResponse<R>> {
    return this.instance.patch(path, data, config);
  }

  public delete<T, R = AxiosResponse<T>>(path: string, data?: T): Promise<R> {
    return this.instance.delete(path, { data });
  }
}

const apiService = new ApiService();

export default apiService;
